import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { AuthService } from '@services/auth.service'
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatestWith,
    first,
    map,
    takeUntil,
} from 'rxjs'
import {
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit, Optional,
    QueryList, TemplateRef,
    ViewChildren,
} from '@angular/core'
import { ContextService } from '@services/context.service'
import { Helper } from '@app/helpers/utilities/helper'
import { LocalStorageService } from '@services/local-storage-service'
import { MenuItem } from '@app/root/components/menu/menuItem'
import { NgbActiveOffcanvas, NgbNav, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { PATHS } from '@app/routes'
import { InboxNotificationCountPublisher } from '@app/domain/InboxNotificationCountPublisher'
import { ToolbarService } from '@services/toolbar.service'
import { ThemeService, ThemePreference } from '@services/theme.service'
import { Venue } from '@app/domain/Venue'
import { AccountUser, UserRole } from '@app/domain/AccountUser'
import { BillingDetails } from '@app/domain/BillingDetails'
import { BillingService } from '@app/services/billing.service'

@Component({
    selector: 'app-menu, [app-menu]',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.sass'],
})
export class MenuComponent implements OnInit, OnDestroy {

    @Input() activeRoute!: string
    @ViewChildren(NgbNav) ngbNavs!: QueryList<NgbNav>
    @HostBinding('class') class = 'vstack h-100'
    appMenu: MenuItem[]
    managementMenu: MenuItem[] = []
    accountMenu: MenuItem[] = []
    venue$!: Observable<Venue>
    user$!: Observable<AccountUser>
    compact = new BehaviorSubject<boolean>(true)
    compact$ = this.compact.asObservable()
    showCompactToggle$!: Observable<boolean>
    fixedWidth$!: Observable<boolean>
    visibleAppMenu$!: Observable<MenuItem[]>
    visibleManagementMenu$!: Observable<MenuItem[]>
    visibleAccountMenu$!: Observable<MenuItem[]>
    billingDetails$!: Observable<BillingDetails>
    private onDestroy$ = new Subject<void>()

    constructor(
        private authService: AuthService,
        private billingService: BillingService,
        private contextService: ContextService,
        private localStorageService: LocalStorageService,
        public inboxNotificationCountPublisher: InboxNotificationCountPublisher,
        public route: ActivatedRoute,
        private router: Router,
        protected toolbarService: ToolbarService,
        public themeService: ThemeService,
        @Optional() private offCanvas: NgbActiveOffcanvas | null,
        @Optional() public offCanvasService: NgbOffcanvas | null
    ) {
        this.billingDetails$ = this.billingService.getCachedBillingDetails()
        this.appMenu = [
            new MenuItem(
                'Activity',
                'activity',
                PATHS.activity
            ),
            new MenuItem(
                'Diary',
                'diary',
                PATHS.diary
            ),
            new MenuItem(
                'Inbox',
                'inbox',
                PATHS.inbox,
                true,
                this.inboxNotificationCountPublisher,
                (_, venue) => {
                    return !venue.canBeBookedWithManualAcceptance() 
                    && !venue.communicationPreferences.messagesEnabled
                }
            ),
            new MenuItem(
                'Orders',
                'food-drink',
                PATHS.orders,
                false,
                null,
                (billingDetails, _) => {
                    return !billingDetails.hasOrdersAddon()
                }   
            ),
            new MenuItem(
                'Vouchers',
                'gift-voucher',
                PATHS.vouchers,
                false,
                null,
                (billingDetails, _) => {
                    return !billingDetails.hasVouchersAddon()
                }   
            ),
        ]

        this.managementMenu = [
            new MenuItem(
                'Customers',
                'people',
                PATHS.customers
            ),
            new MenuItem(
                'Reports',
                'reports',
                PATHS.reports,
                false,
                null,
                undefined,
                UserRole.MANAGER
            ),
        ]

        this.accountMenu = [
            new MenuItem(
                'Settings',
                'settings',
                `${PATHS.management}`,
                false,
                null,
                undefined,
                UserRole.MANAGER
            ),
            new MenuItem(
                'Account',
                'profile',
                PATHS.profile,
                false,
                null,
                undefined,
                UserRole.MANAGER
            ),
        ]
    }

    get allMenuItems(): MenuItem[] {
        return this.appMenu
            .concat(this.managementMenu)
            .concat(this.accountMenu)
    }

    ngOnInit() {
        this.bindFixedWidthToBeingExpandedOnSideOrCompact()
        this.venue$ = this.contextService.venue$
        this.user$ = this.contextService.user$
        this.bindRouteToMenu()
        this.setMenuSizeToLocalStorage()
        this.bindVenueToMenuItems()
        this.bindShowCompactToggleToBeingOnSide()
        this.setupRoleBasedMenuFiltering()
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    openOffCanvasMenu(content: TemplateRef<any>) {
        this.offCanvasService?.open(content, { panelClass: 'w-75 overflow-auto' })
    }

    dismiss() {
        this.offCanvas?.dismiss()
    }

    hideSideMenu() {
        this.toolbarService.setToolbarPlacement('top')
        this.menuExpandClicked()
    }

    showSideMenu() {
        this.toolbarService.setToolbarPlacement('side')
        this.offCanvas?.dismiss()
    }

    menuExpandClicked() {
        this.compact.next(false)
        this.localStorageService.setItem('menuSize', 'expanded')
    }

    menuCollapseClicked() {
        this.compact.next(true)
        this.localStorageService.setItem('menuSize', 'compact')
    }

    logout() {
        this.authService.logout()
    }

    cycleTheme() {
        const order: ThemePreference[] = ['system', 'light', 'dark']
        const current = this.themeService.currentPreference
        const next = order[(order.indexOf(current) + 1) % order.length]
        this.themeService.setPreference(next)
    }

    private bindRouteToMenu() {
        this.router.events.pipe(
            first(event => event instanceof NavigationEnd),
            map(event => event as NavigationEnd)
        )
            .subscribe(event => {
                const path = Helper.relativePath(event.url)
                const allMenuItems = this.appMenu
                    .concat(this.managementMenu)
                    .concat(this.accountMenu)
                for (const menuItem of allMenuItems) {
                    this.selectMenuItemIfShown(menuItem, path)
                }
            })
    }

    private bindVenueToMenuItems() {
        this.venue$
            .pipe(
                takeUntil(this.onDestroy$),
                combineLatestWith(this.billingDetails$)
            )
            .subscribe(([venue, bilingDetails]) => {
                this.allMenuItems.forEach(menuItem => {
                    menuItem.configure(bilingDetails, venue)
                })
                this.inboxNotificationCountPublisher.refreshNow()
            })
    }

    private bindFixedWidthToBeingExpandedOnSideOrCompact() {
        this.fixedWidth$ = this.toolbarService.toolbarPlacement$
            .pipe(
                takeUntil(this.onDestroy$),
                combineLatestWith(this.compact$),
                map(([placement, isCompact]) => {
                    return placement === 'side' && !isCompact
                })
            )
    }

    private bindShowCompactToggleToBeingOnSide() {
        this.showCompactToggle$ = this.toolbarService.toolbarPlacement$
            .pipe(
                takeUntil(this.onDestroy$),
                map(placement => placement === 'side')
            )
    }

    private selectMenuItemIfShown(item: MenuItem, path: string): boolean {
        if (path.startsWith(item.route)) {
            this.activeRoute = item.route
            return true
        }
        return false
    }

    private setMenuSizeToLocalStorage() {
        const menuSize = this.localStorageService.getItem('menuSize')
        if (menuSize === 'compact') {
            this.compact.next(true)
        } else if (menuSize === 'expanded') {
            this.compact.next(false)
        }
    }

    private setupRoleBasedMenuFiltering() {
        this.visibleAppMenu$ = this.user$.pipe(
            map(user => {
                return this.appMenu.filter(menuItem => 
                    !menuItem.requiredRole || this.userHasRequiredRole(user, menuItem.requiredRole)
                )
            })
        )

        this.visibleManagementMenu$ = this.user$.pipe(
            map(user => {
                return this.managementMenu.filter(menuItem => 
                    !menuItem.requiredRole || this.userHasRequiredRole(user, menuItem.requiredRole)
                )
            })
        )

        this.visibleAccountMenu$ = this.user$.pipe(
            map(user => {
                return this.accountMenu.filter(menuItem => 
                    !menuItem.requiredRole || this.userHasRequiredRole(user, menuItem.requiredRole)
                )
            })
        )
    }

    private userHasRequiredRole(user: AccountUser, requiredRole: UserRole): boolean {
        const roleHierarchy = [
            UserRole.STAFF,
            UserRole.MANAGER, 
            UserRole.OWNER,
        ]
        const userRoleIndex = roleHierarchy.indexOf(user.role)
        const requiredRoleIndex = roleHierarchy.indexOf(requiredRole)
        
        return userRoleIndex >= requiredRoleIndex
    }
}
